import { Grid } from '@material-ui/core'
import FileFieldClearable from 'elements/field/FileFieldClearable'
import { List } from 'elements/react-admin/List'
import { ComponentVersions_3_0, ComponentVersionsInherit, Typography } from 'opensolar-ui'
import { useEffect, useState } from 'react'
import {
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  Edit,
  EditButton,
  FileField,
  FileInput,
  FunctionField,
  NumberInput,
  required,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  useTranslate,
} from 'react-admin'
import restClient from 'restClient'

const restClientInstance = restClient(window.API_ROOT + '/api')

export const PricingScheduleList = (props) => {
  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <List hasSearch={true} {...props}>
        <Datagrid>
          <TextField source="title" />
          <TextField source="country_name" label="Country" />
          <BooleanField source="is_default" label="Default" />
          <TextField source="payout_fee" label="Payout Fee" />
          <FunctionField
            source="created_at"
            label="Created"
            render={(record: any) => new Date(record.created_at).toLocaleDateString()}
          />
          <EditButton useListStyle={true} />
        </Datagrid>
      </List>
    </ComponentVersionsInherit>
  )
}

const PricingScheduleInputFields = (props) => {
  const translate = useTranslate()
  const [cohorts, setCohorts] = useState([])
  const [cohortsLoading, setCohortsLoading] = useState(true)

  useEffect(() => {
    // Cohorts are global, not org-specific - use direct URL without org_id
    restClientInstance('CUSTOM_GET', 'custom', {
      url: 'cashflow_pricing_schedules/available_cohorts/',
    })
      .then((response) => {
        if (response.data && response.data.cohorts) {
          setCohorts(response.data.cohorts)
        }
        setCohortsLoading(false)
      })
      .catch((error) => {
        console.error('Failed to fetch cohorts:', error)
        setCohortsLoading(false)
      })
  }, [])

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextInput source="title" validate={[required()]} fullWidth label={translate('Schedule Title')} />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          source="country_iso2"
          validate={[required()]}
          label={translate('Country Code')}
          fullWidth
          helperText={'Enter 2-letter ISO country code (e.g., AU, US, GB)'}
          placeholder="e.g., AU, US, GB"
        />
      </Grid>
      <Grid item xs={6}>
        <BooleanInput source="is_default" label={translate('Set as Default for Country')} />
      </Grid>
      <Grid item xs={6}>
        <NumberInput source="payout_fee" label="Payout Fee ($)" step={0.01} defaultValue={0} fullWidth />
      </Grid>
      <Grid item xs={6}>
        <SelectInput
          source="cohort"
          label="BlueSnap Partner Cohort"
          choices={cohorts}
          optionText="name"
          optionValue="id"
          fullWidth
          validate={[required()]}
          helperText="Select the BlueSnap cohort for this pricing schedule"
          disabled={cohortsLoading}
        />
      </Grid>
      <Grid item xs={12}>
        <FileFieldClearable
          record={props.record}
          label="Pricing Agreement PDF"
          source="pricing_agreement_pdf"
          maxWidth={200}
        />

        <FileInput
          source="pricing_agreement_pdf"
          label="Pricing Agreement PDF"
          placeholder={<Typography variant="body2">Click to upload or drag & drop</Typography>}
          accept=".pdf"
        >
          <FileField source="src" title="title" />
        </FileInput>
      </Grid>

      {/* Credit Card Standard */}
      <Grid item xs={12}>
        <h3>Credit Card (Standard)</h3>
      </Grid>
      <Grid item xs={4}>
        <NumberInput
          source="credit_card_standard_percentage_fee"
          label="Percentage Fee"
          step={0.01}
          defaultValue={0}
          fullWidth
          placeholder="e.g., 2.7 for 2.7%"
          helperText={'e.g., 2.7 for 2.7%'}
        />
      </Grid>
      <Grid item xs={4}>
        <NumberInput
          source="credit_card_standard_fixed_fee"
          label="Fixed Fee ($)"
          step={0.01}
          defaultValue={0}
          fullWidth
        />
      </Grid>
      <Grid item xs={4}>
        <NumberInput source="credit_card_standard_max_fee" label="Max Fee Cap ($)" step={0.01} fullWidth />
      </Grid>

      {/* Credit Card Premium */}
      <Grid item xs={12}>
        <h3>Credit Card (Premium)</h3>
      </Grid>
      <Grid item xs={4}>
        <NumberInput
          source="credit_card_premium_percentage_fee"
          label="Percentage Fee"
          step={0.01}
          defaultValue={0}
          fullWidth
          placeholder="e.g., 2.7 for 2.7%"
          helperText={'e.g., 2.7 for 2.7%'}
        />
      </Grid>
      <Grid item xs={4}>
        <NumberInput
          source="credit_card_premium_fixed_fee"
          label="Fixed Fee ($)"
          step={0.01}
          defaultValue={0}
          fullWidth
        />
      </Grid>
      <Grid item xs={4}>
        <NumberInput source="credit_card_premium_max_fee" label="Max Fee Cap ($)" step={0.01} fullWidth />
      </Grid>

      {/* Credit Card International */}
      <Grid item xs={12}>
        <h3>Credit Card (International)</h3>
      </Grid>
      <Grid item xs={4}>
        <NumberInput
          source="credit_card_international_percentage_fee"
          label="Percentage Fee"
          step={0.01}
          defaultValue={0}
          fullWidth
          placeholder="e.g., 2.7 for 2.7%"
          helperText={'e.g., 2.7 for 2.7%'}
        />
      </Grid>
      <Grid item xs={4}>
        <NumberInput
          source="credit_card_international_fixed_fee"
          label="Fixed Fee ($)"
          step={0.01}
          defaultValue={0}
          fullWidth
        />
      </Grid>
      <Grid item xs={4}>
        <NumberInput source="credit_card_international_max_fee" label="Max Fee Cap ($)" step={0.01} fullWidth />
      </Grid>

      {/* Debit Card Standard */}
      <Grid item xs={12}>
        <h3>Debit Card (Standard)</h3>
      </Grid>
      <Grid item xs={4}>
        <NumberInput
          source="debit_card_standard_percentage_fee"
          label="Percentage Fee"
          step={0.01}
          defaultValue={0}
          fullWidth
          placeholder="e.g., 2.7 for 2.7%"
          helperText={'e.g., 2.7 for 2.7%'}
        />
      </Grid>
      <Grid item xs={4}>
        <NumberInput
          source="debit_card_standard_fixed_fee"
          label="Fixed Fee ($)"
          step={0.01}
          defaultValue={0}
          fullWidth
        />
      </Grid>
      <Grid item xs={4}>
        <NumberInput source="debit_card_standard_max_fee" label="Max Fee Cap ($)" step={0.01} fullWidth />
      </Grid>

      {/* Debit Card Premium */}
      <Grid item xs={12}>
        <h3>Debit Card (Premium)</h3>
      </Grid>
      <Grid item xs={4}>
        <NumberInput
          source="debit_card_premium_percentage_fee"
          label="Percentage Fee"
          step={0.01}
          defaultValue={0}
          fullWidth
          placeholder="e.g., 2.7 for 2.7%"
          helperText={'e.g., 2.7 for 2.7%'}
        />
      </Grid>
      <Grid item xs={4}>
        <NumberInput
          source="debit_card_premium_fixed_fee"
          label="Fixed Fee ($)"
          step={0.01}
          defaultValue={0}
          fullWidth
        />
      </Grid>
      <Grid item xs={4}>
        <NumberInput source="debit_card_premium_max_fee" label="Max Fee Cap ($)" step={0.01} fullWidth />
      </Grid>

      {/* Debit Card International */}
      <Grid item xs={12}>
        <h3>Debit Card (International)</h3>
      </Grid>
      <Grid item xs={4}>
        <NumberInput
          source="debit_card_international_percentage_fee"
          label="Percentage Fee"
          step={0.01}
          defaultValue={0}
          fullWidth
          placeholder="e.g., 2.7 for 2.7%"
          helperText={'e.g., 2.7 for 2.7%'}
        />
      </Grid>
      <Grid item xs={4}>
        <NumberInput
          source="debit_card_international_fixed_fee"
          label="Fixed Fee ($)"
          step={0.01}
          defaultValue={0}
          fullWidth
        />
      </Grid>
      <Grid item xs={4}>
        <NumberInput source="debit_card_international_max_fee" label="Max Fee Cap ($)" step={0.01} fullWidth />
      </Grid>

      {/* ACH */}
      <Grid item xs={12}>
        <h3>ACH / Bank Transfer</h3>
      </Grid>
      <Grid item xs={4}>
        <NumberInput
          source="ach_percentage_fee"
          label="Percentage Fee"
          step={0.01}
          defaultValue={0}
          fullWidth
          placeholder="e.g., 2.7 for 2.7%"
          helperText={'e.g., 2.7 for 2.7%'}
        />
      </Grid>
      <Grid item xs={4}>
        <NumberInput source="ach_fixed_fee" label="Fixed Fee ($)" step={0.01} defaultValue={0} fullWidth />
      </Grid>
      <Grid item xs={4}>
        <NumberInput source="ach_max_fee" label="Max Fee Cap ($)" step={0.01} fullWidth />
      </Grid>

      {/* BECS Direct Debit (AU) */}
      <Grid item xs={12}>
        <h3>BECS Direct Debit (Australia)</h3>
      </Grid>
      <Grid item xs={4}>
        <NumberInput
          source="becs_direct_debit_percentage_fee"
          label="Percentage Fee"
          step={0.01}
          defaultValue={0}
          fullWidth
          placeholder="e.g., 2.7 for 2.7%"
          helperText={'e.g., 2.7 for 2.7%'}
        />
      </Grid>
      <Grid item xs={4}>
        <NumberInput
          source="becs_direct_debit_fixed_fee"
          label="Fixed Fee (AUD)"
          step={0.01}
          defaultValue={0}
          fullWidth
        />
      </Grid>
      <Grid item xs={4}>
        <NumberInput source="becs_direct_debit_max_fee" label="Max Fee Cap (AUD)" step={0.01} fullWidth />
      </Grid>
    </Grid>
  )
}

export const PricingScheduleEdit = (props) => (
  <ComponentVersionsInherit versions={ComponentVersions_3_0}>
    <Edit {...props}>
      <SimpleForm>
        <PricingScheduleInputFields {...props} />
      </SimpleForm>
    </Edit>
  </ComponentVersionsInherit>
)

export const PricingScheduleCreate = (props) => {
  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <Create {...props}>
        <SimpleForm>
          <PricingScheduleInputFields {...props} />
        </SimpleForm>
      </Create>
    </ComponentVersionsInherit>
  )
}
